import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    InputAdornment,
    MenuItem,
    Pagination,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import { Launch, NewReleases, Search } from "@mui/icons-material";
import noticeService from "../../services/noticeService";
import { filterNotices, getSafeNoticeUrl, paginateItems } from "../../utils/noticeUtils";

const priorityColorMap = {
    high: "#C1272D",
    medium: "#f59e0b",
    low: "#0ea5e9"
};

const readKeyForUser = (uid) => `notice_read_${uid || "guest"}`;

export default function StudentNoticeBoard({ userId }) {
    const [allNotices, setAllNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [priority, setPriority] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [page, setPage] = useState(1);
    const [readIds, setReadIds] = useState([]);
    const pageSize = 6;

    useEffect(() => {
        const saved = localStorage.getItem(readKeyForUser(userId));
        if (saved) {
            try {
                setReadIds(JSON.parse(saved));
            } catch (parseError) {
                setReadIds([]);
            }
        }
        const unsub = noticeService.subscribeVisibleNotices(
            {},
            (notices) => {
                setAllNotices(notices);
                setLoading(false);
            },
            (err) => {
                setError(err?.message || "Failed to load notices");
                setLoading(false);
            }
        );
        return () => {
            if (typeof unsub === "function") unsub();
        };
    }, [userId]);

    useEffect(() => {
        setPage(1);
    }, [search, priority, startDate, endDate]);

    const filteredNotices = useMemo(
        () =>
            filterNotices(allNotices, {
                query: search,
                priority,
                startDate,
                endDate
            }),
        [allNotices, search, priority, startDate, endDate]
    );

    const paginated = useMemo(
        () => paginateItems(filteredNotices, page, pageSize),
        [filteredNotices, page]
    );

    const markAsRead = (noticeId) => {
        if (readIds.includes(noticeId)) return;
        const updated = [...readIds, noticeId];
        setReadIds(updated);
        localStorage.setItem(readKeyForUser(userId), JSON.stringify(updated));
    };

    const handleOpenNoticeLink = (notice) => {
        const safeUrl = getSafeNoticeUrl(notice.linkUrl);
        if (!safeUrl) {
            setError("This notice link is invalid or unavailable.");
            return;
        }
        markAsRead(notice.id);
        window.open(safeUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <Card variant="outlined" sx={{ mb: 2.5 }}>
            <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#003366", mb: 1 }}>
                    Notice Board
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Stay updated with announcements, deadlines, and important actions.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

                <Grid container spacing={1.25} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            label="Search notices"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            select
                            label="Priority"
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="low">Low</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="From"
                            InputLabelProps={{ shrink: true }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="To"
                            InputLabelProps={{ shrink: true }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </Grid>
                </Grid>

                {loading ? (
                    <Alert severity="info">Loading latest notices...</Alert>
                ) : filteredNotices.length === 0 ? (
                    <Alert severity="info">No notices found for current filters.</Alert>
                ) : (
                    <>
                        <Grid container spacing={1.5}>
                            {paginated.items.map((notice) => {
                                const isUnread = !readIds.includes(notice.id);
                                return (
                                    <Grid item xs={12} md={6} key={notice.id}>
                                        <Box
                                            role="article"
                                            tabIndex={0}
                                            sx={{
                                                height: "100%",
                                                p: 1.5,
                                                border: "1px solid #e5e7eb",
                                                borderLeft: "4px solid",
                                                borderLeftColor: priorityColorMap[notice.priority] || "#0ea5e9",
                                                borderRadius: 1.5,
                                                transition: "all 0.2s ease",
                                                "&:hover": {
                                                    boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
                                                    transform: "translateY(-2px)"
                                                }
                                            }}
                                        >
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography sx={{ fontWeight: 700, color: "#003366" }}>{notice.title}</Typography>
                                                    {isUnread && (
                                                        <Chip
                                                            icon={<NewReleases sx={{ fontSize: 14 }} />}
                                                            size="small"
                                                            label="New"
                                                            sx={{ bgcolor: "#fff3cd", color: "#8a6d3b", fontWeight: 600 }}
                                                        />
                                                    )}
                                                </Stack>
                                                <Chip
                                                    size="small"
                                                    label={String(notice.priority || "low").toUpperCase()}
                                                    sx={{
                                                        bgcolor: `${priorityColorMap[notice.priority] || "#0ea5e9"}22`,
                                                        color: priorityColorMap[notice.priority] || "#0ea5e9",
                                                        fontWeight: 700
                                                    }}
                                                />
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {notice.contentText}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                                Published: {notice.createdAt ? new Date(notice.createdAt).toLocaleString() : "N/A"}
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                endIcon={<Launch />}
                                                onClick={() => handleOpenNoticeLink(notice)}
                                                sx={{ backgroundColor: "#C1272D", "&:hover": { backgroundColor: "#9b1f24" } }}
                                            >
                                                Open Notice Link
                                            </Button>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                        {paginated.totalPages > 1 && (
                            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                                <Pagination
                                    page={paginated.page}
                                    count={paginated.totalPages}
                                    onChange={(_, newPage) => setPage(newPage)}
                                    color="primary"
                                    shape="rounded"
                                />
                            </Stack>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
